/* global tinymce */

import React from 'react';
import { Button, Glyphicon } from 'react-bootstrap';
import ListEditor from './list-editor';
import TypeSource from '../../sources/type';
import moment from 'moment';
import '../../../css/object-editor.less';

class ObjectEditor extends React.Component {

    constructor(props) {
        super(props);

        this.state = { type: null, isLoading: true };

        this.onNew = this.onNew.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onScalarChange = this.onScalarChange.bind(this);
        this.renderProperty = this.renderProperty.bind(this);
        this.onChildChange = this.onChildChange.bind(this);
        this.onDatePickerMounted = this.onDatePickerMounted.bind(this);
        this.onContentMounted = this.onContentMounted.bind(this);
    }

    // Every time a list editor mounts we need to know about the type
    // associated to its value, this allows for adding new list items.
    // To get the type info we need to hit the api
    componentDidMount() {
        this.setState({ isLoading: true });
        if (this.props.typeElementId) {
            TypeSource.get(this.props.typeElementId, this.props.authenticationToken, this.props.tenantId)
                .then((response) => {
                    this.setState({ type: response, isLoading: false });

                    // Checking to see if any properties on the type have been added or taken away
                    if (this.props.objectData && (response.properties.length !== this.props.objectData[0].properties.length)) {

                        // If a new property has been added then we need to inject it
                        // into the object data for it to be displayed
                        response.properties.forEach((typeProperty) => {
                            const property = this.props.objectData[0].properties.find(prop => prop.typeElementPropertyId === typeProperty.id);
                            if (!property) {
                                this.props.objectData.forEach((objectData) => {
                                    objectData.properties.push({
                                        contentType: typeProperty.contentType,
                                        contentValue: null,
                                        developerName: typeProperty.developerName,
                                        objectData: null,
                                        typeElementId: null,
                                        typeElementPropertyId: typeProperty.id,
                                    });
                                });
                            }
                        });

                        // If a property has been removed from the type
                        // we want to remove it from being displayed
                        this.props.objectData.forEach(
                            objectData => objectData.properties.filter(
                                property => response.properties.find(
                                    prop => prop.id === property.typeElementPropertyId,
                                ),
                            ),
                        );

                        this.props.onChange({ id: this.props.id, objectData: this.props.objectData, index: this.props.index });
                    }
                });
        }
    }

    /**
     * @description Instantiates the Bootstrap date picker and binds it to a DOM node
     */
    onDatePickerMounted(input) {
        if (input) {
            $(input).datetimepicker({
                locale: 'en-us',
                date: input.value,
                useCurrent: false,
            }).on('dp.change', this.onScalarChange);
        }
    }

    /**
     * @description Instantiates the tinyMCE editor and binds it to a DOM node
     */
    onContentMounted(node) {
        if (node) {
            const self = this;
            tinymce.init({
                selector: `#${node.id}`,
                plugins: manywho.settings.global('richtext.plugins', this.props.flowKey, []),
                toolbar: manywho.settings.global('richtext.toolbar', this.props.flowKey, []),
                importcss_append: true,
                setup: (editor) => {
                    editor.on('init', function setFont() {
                        this.getDoc().body.style.fontSize = manywho.settings.global('richtext.fontsize', self.props.flowKey, '13px');
                    });
                    editor.on('change', self.onScalarChange);
                },
            });

            this.props.addEditor(node.id);
        }
    }

    /**
     * @description To add a object
     */
    onNew() {
        const objectData = {
            developerName: this.state.type.developerName,
            typeElementId: this.state.type.id,
            key: manywho.utils.guid(),
            properties: this.state.type.properties.map(property => ({
                developerName: property.developerName,
                contentValue: null,
                contentFormat: property.contentFormat,
                contentType: property.contentType,
                typeElementPropertyId: property.id,
                typeElementId: property.typeElementId,
                typeElementDeveloperName: property.typeElementDeveloperName,
                objectData: null,
            })),
        };

        this.props.onChange({ id: this.props.id, objectData: [objectData], index: this.props.index });
    }

    /**
     * @description To remove an object
     */
    onDelete() {
        this.props.onChange({ id: this.props.id, objectData: null, index: this.props.index });
    }

    /**
     * @description Handles the mutation of objectData property content values
     * where the value is not of complex type e.g. String, Integer etc
     */
    onScalarChange(e) {
        const typeElementPropertyId = e.target.getAttribute ?
            e.target.getAttribute('data-typeelementpropertyid') : e.target.targetElm.getAttribute('data-typeelementpropertyid');
        const property = this.props.objectData[0].properties.find(prop => prop.typeElementPropertyId === typeElementPropertyId);

        switch (property.contentType.toUpperCase()) {
        case manywho.component.contentTypes.string:
        case manywho.component.contentTypes.encrypted:
        case manywho.component.contentTypes.password:
        case manywho.component.contentTypes.number:
        default:
            property.contentValue = e.target.value;
            break;

        case manywho.component.contentTypes.boolean:
            property.contentValue = e.target.checked;
            break;

        case manywho.component.contentTypes.datetime: {
            const date = moment(e.target.value, [moment.ISO_8601, 'MM/DD/YYYY HH:mm:ss']);
            property.contentValue = date.isValid() ? date.format() : null;
            break;
        }
        case manywho.component.contentTypes.content:
            property.contentValue = tinymce.activeEditor.getContent();
            break;
        }

        this.props.onChange({ id: this.props.id, objectData: this.props.objectData, index: this.props.index });
    }

    /**
     * @description Identifies the objectData property that has been modified and mutates it
     * to the new objectData before calling back to update the objectData in state (kind of like the reducer)
     */
    onChildChange({ id, objectData }) {
        const property = this.props.objectData[0].properties.find(prop => prop.typeElementPropertyId === id);
        property.objectData = objectData;
        this.props.onChange({ id: this.props.id, objectData: this.props.objectData, index: this.props.index });
    }

    /**
     * @description Determines what element to render based on objectData property
     */
    renderProperty(property) {
        let element = null;
        switch (property.contentType.toUpperCase()) {
        case manywho.component.contentTypes.string:
        default:
            element = (
                <textarea
                    id={manywho.utils.guid()}
                    key={property.typeElementPropertyId}
                    data-typeelementpropertyid={property.typeElementPropertyId}
                    className="form-control"
                    type="text"
                    onChange={this.onScalarChange}
                    value={property.contentValue}
                    rows={2}
                />
            );
            break;

        case manywho.component.contentTypes.number:
            element = (
                <input
                    id={manywho.utils.guid()}
                    key={property.typeElementPropertyId}
                    data-typeelementpropertyid={property.typeElementPropertyId}
                    className="form-control"
                    type="number"
                    onChange={this.onScalarChange}
                    value={property.contentValue}
                />
            );
            break;

        case manywho.component.contentTypes.boolean: {

            // This is because boolean default content values are referenced as
            // strings inside of the model, whilst they are not inside the state
            const booleanContentValue = typeof property.contentValue === 'boolean'
                ? property.contentValue
                : typeof property.contentValue === 'string'
                    ? property.contentValue.toLowerCase() === 'true'
                    : false;

            element = (
                <input
                    id={manywho.utils.guid()}
                    key={property.typeElementPropertyId}
                    data-typeelementpropertyid={property.typeElementPropertyId}
                    type="checkbox"
                    onChange={this.onScalarChange}
                    defaultChecked={booleanContentValue}
                />
            );
            break;
        }

        case manywho.component.contentTypes.encrypted:
        case manywho.component.contentTypes.password:
            element = (
                <input
                    id={manywho.utils.guid()}
                    key={property.typeElementPropertyId}
                    data-typeelementpropertyid={property.typeElementPropertyId}
                    className="form-control"
                    type="password"
                    onChange={this.onScalarChange}
                    value={property.contentValue}
                />
            );
            break;

        case manywho.component.contentTypes.datetime:
            element = (
                <input
                    id={manywho.utils.guid()}
                    key={property.typeElementPropertyId}
                    data-typeelementpropertyid={property.typeElementPropertyId}
                    className="form-control"
                    type="text"
                    defaultValue={property.contentValue}
                    ref={this.onDatePickerMounted}
                />
            );
            break;

        case manywho.component.contentTypes.content:
            element = (
                <textarea
                    id={manywho.utils.guid()}
                    key={property.typeElementPropertyId}
                    data-typeelementpropertyid={property.typeElementPropertyId}
                    className="form-control"
                    ref={this.onContentMounted}
                    onChange={this.onScalarChange}
                    value={property.contentValue}
                />
            );
            break;

        case manywho.component.contentTypes.object:

            element = (
                <div id={property.typeElementPropertyId} key={property.id} className="object-editor-column">
                    <ObjectEditor
                        id={property.typeElementPropertyId}
                        typeElementId={property.typeElementId}
                        objectData={property.objectData}
                        onChange={this.onChildChange}
                        flowKey={this.props.flowKey}
                        authenticationToken={this.props.authenticationToken}
                        tenantId={this.props.tenantId}
                        isTypeNameVisible
                        isLoading={this.props.isLoading}
                        addEditor={this.props.addEditor}
                    />
                </div>
            );
            break;

        case manywho.component.contentTypes.list:

            element = (
                <div id={property.typeElementPropertyId} key={property.typeElementPropertyId} className="list-editor-column">
                    <ListEditor
                        id={property.typeElementPropertyId}
                        typeElementId={property.typeElementId}
                        objectData={property.objectData}
                        onChange={this.onChildChange}
                        flowKey={this.props.flowKey}
                        isLoading={this.props.isLoading}
                        authenticationToken={this.props.authenticationToken}
                        isTypeNameVisible
                        addEditor={this.props.addEditor}
                    />
                </div>
            );
            break;
        }

        return element;
    }

    render() {
        manywho.log.info('Rendering Object Editor');

        let typeName = null;
        if (this.props.isTypeNameVisible && (this.props.objectData || this.state.type)) {
            let typeElementDeveloperName = this.state.type ? this.state.type.developerName : null;

            if (manywho.utils.isNullOrEmpty(typeElementDeveloperName) && this.props.objectData && this.props.objectData.length > 0) {
                typeElementDeveloperName = this.props.objectData[0].developerName;
            }
            typeName = <h5>{`Type: ${typeElementDeveloperName}`}</h5>;
        }

        return (
            <div className="object-editor">
                {this.state.isLoading ?
                    React.createElement(manywho.component.getByName('wait'), { isVisible: true }, null)
                    :
                    (
                        <React.Fragment>
                            {typeName}
                            {this.props.objectData && this.props.objectData.length > 0 ?
                                <Button bsStyle="danger" bsSize="small" onClick={this.onDelete}><Glyphicon glyph="trash" /></Button> :
                                <Button bsStyle="primary" bsSize="small" onClick={this.onNew}><Glyphicon glyph="plus" /></Button>
                            }
                            {this.props.objectData && this.props.objectData.length > 0 ? (
                                <table className="table table-bordered table-striped">
                                    <tbody>
                                        <tr key="header">
                                            <th>Property</th>
                                            <th>Value</th>
                                        </tr>
                                        {this.props.objectData[0].properties.map(prop => (
                                            <tr key={prop.id}>
                                                <td>{prop.developerName}</td>
                                                <td>{this.renderProperty(prop)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) :
                                null
                            }
                        </React.Fragment>
                    )
                }
            </div>
        );
    }

}

ObjectEditor.defaultProps = {
    isTypeNameVisible: false,
};

export default ObjectEditor;
